// pages/monitor/panels/peak/PeakSystem.js —— 削峰填谷 · 系统管理（依据《云平台单站控制（削峰填谷）需求文档》p41-57）
// 覆盖：电站信息管理 / 设备管理（修改设备名称 · 更换电表 · DIO 信息）/ 强电结构 / 日志管理 /
//       EMS 时段管理 / 电价管理 / 日前电价 / 拓扑图配置 / 二次口令（供控制中心与设备读写校验）。
import React, { useState, useEffect, useRef } from 'react';
import { Table, Select, Input, Button, Space, Tag, Radio, DatePicker, message, Modal, InputNumber, Dropdown } from 'antd';
import ScrollTabs from '../../../../components/ScrollTabs';
import { EditOutlined, EnvironmentOutlined, FolderOutlined } from '@ant-design/icons';
import {
  EMS_PERIODS, TARIFF_ROWS, getSecPwdPolicy, SEC_PWD_RECORDS, getDevices, getDayAhead,
} from '../../../../config/stationData';
import { FIRE_IO, PEAK_LOGS } from './data';
import './peak.css';

// ---------------- 电站信息管理 ----------------
const StationInfoView = ({ station }) => {
  const [form, setForm] = useState({
    name: station.name, location: station.location, onlineAt: station.onlineAt,
    power: station.rated.power, capacity: station.rated.capacity,
  });
  const rows = [
    ['电站名称', 'name'], ['位置', 'location'], ['投运时间', 'onlineAt'], ['装机功率', 'power'], ['装机容量', 'capacity'],
  ];
  return (
    <div className="panel" style={{ maxWidth: 760 }}>
      <div className="panel-title">电站信息管理<small>修改后提交，用于展示与报表口径</small></div>
      <iframe
        title="电站位置地图"
        src={`https://uri.amap.com/marker?position=120.79,30.71&name=${encodeURIComponent(station.name)}&coordinate=gaode&callnative=false`}
        style={{ width: '100%', height: 280, border: '1px solid rgba(0,212,255,0.25)', borderRadius: 8, marginBottom: 14, background: '#16213a', display: 'block' }}
      />
      {rows.map(([label, key]) => (
        <div className="peak-form-row" key={key}>
          <span className="peak-form-label">{label}</span>
          <Input size="small" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} style={{ width: 280 }} />
        </div>
      ))}
      <div className="form-grid">
        <div className="form-item"><span className="form-item-label">电站类型</span><span className="form-item-value">{station.typeLabel}</span></div>
        <div className="form-item"><span className="form-item-label">电压等级</span><span className="form-item-value">{station.grid.voltage}</span></div>
        <div className="form-item"><span className="form-item-label">变压器</span><span className="form-item-value">{station.grid.transformer}</span></div>
        <div className="form-item"><span className="form-item-label">电站年龄</span><span className="form-item-value">{station.age} 天</span></div>
      </div>
      <Button size="small" type="primary" style={{ marginTop: 14 }} onClick={() => message.success('电站信息已保存（演示）')}>保存</Button>
    </div>
  );
};

// ---------------- 设备管理 ----------------
const DeviceManageView = ({ station }) => {
  const dev = getDevices(station.id);
  const [sub, setSub] = useState('name');
  const [target, setTarget] = useState(dev.pcs[0].id);
  const [newName, setNewName] = useState('');
  const [meterPoint, setMeterPoint] = useState(dev.meters[0].id);
  const [meterTo, setMeterTo] = useState(dev.meters[1].id);
  const [ioIdx, setIoIdx] = useState(FIRE_IO[0].id);
  const io = FIRE_IO.find((x) => x.id === ioIdx) || FIRE_IO[0];

  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return (
    <ScrollTabs
      activeKey={sub}
      onChange={setSub}
      items={[
        {
          key: 'name',
          label: '修改设备名称',
          children: (
            <div className="panel" style={{ maxWidth: 760 }}>
              <div className="panel-title">修改设备名称<small>展示名称修改后同步到设备监控与强电图</small></div>
              <div className="peak-form-row">
                <span className="peak-form-label">选择设备</span>
                <Select size="small" value={target} onChange={setTarget} style={{ width: 260 }}
                  options={[...dev.pcs, ...dev.racks, ...dev.hvac, dev.liquid].map((d) => ({ value: d.id, label: d.name }))} />
              </div>
              <div className="peak-form-row">
                <span className="peak-form-label">新名称</span>
                <Input size="small" value={newName} onChange={(e) => setNewName(e.target.value)} style={{ width: 260 }} placeholder="请输入新设备名称" />
                <Button size="small" type="primary" onClick={() => { if (!newName) { message.warning('请输入新名称'); return; } message.success('设备名称已修改（演示）'); setNewName(''); }}>保存</Button>
              </div>
            </div>
          ),
        },
        {
          key: 'meter',
          label: '更换电表',
          children: (
            <div className="panel" style={{ maxWidth: 760 }}>
              <div className="panel-title">更换电表<small>更换计量点绑定的电表</small></div>
              <div className="peak-form-row">
                <span className="peak-form-label">计量点</span>
                <Select size="small" value={meterPoint} onChange={setMeterPoint} style={{ width: 260 }}
                  options={['并网点', '储能进线', '负荷出线'].map((p) => ({ value: p, label: p }))} />
              </div>
              <div className="peak-form-row">
                <span className="peak-form-label">更换为</span>
                <Select size="small" value={meterTo} onChange={setMeterTo} style={{ width: 260 }}
                  options={dev.meters.map((m) => ({ value: m.id, label: m.name }))} />
                <Button size="small" type="primary" onClick={() => message.success('电表已更换（演示）')}>确认更换</Button>
              </div>
            </div>
          ),
        },
        {
          key: 'dio',
          label: 'DIO 信息',
          children: (
            <div className="panel" style={{ maxWidth: 860 }}>
              <div className="panel-title">DIO 信息<small>IO 控制器 DI / DO 端口名称可在系统管理修改</small></div>
              <div className="peak-form-row">
                <span className="peak-form-label">IO 控制器</span>
                <Select size="small" value={ioIdx} onChange={setIoIdx} style={{ width: 220 }} options={FIRE_IO.map((x) => ({ value: x.id, label: x.name }))} />
              </div>
              <div className="grid-2">
                <Table size="small" rowKey="key" pagination={false} dataSource={io.di}
                  columns={[
                    { title: 'DI 端口', dataIndex: 'key', width: 90 },
                    { title: '端口名称', dataIndex: 'name', render: (v) => <Input size="small" defaultValue={v} /> },
                    { title: '状态', dataIndex: 'value', width: 80, render: (v) => <Tag color={v === '闭合' ? 'green' : 'default'}>{v}</Tag> },
                  ]}
                />
                <Table size="small" rowKey="key" pagination={false} dataSource={io.do}
                  columns={[
                    { title: 'DO 端口', dataIndex: 'key', width: 90 },
                    { title: '端口名称', dataIndex: 'name', render: (v) => <Input size="small" defaultValue={v} /> },
                    { title: '状态', dataIndex: 'value', width: 80, render: (v) => <Tag color={v === '闭合' ? 'green' : 'default'}>{v}</Tag> },
                  ]}
                />
              </div>
              <Button size="small" type="primary" style={{ marginTop: 14 }} onClick={() => message.success('DIO 名称已保存（演示）')}>保存</Button>
            </div>
          ),
        },
      ]}
    />
  );
};

// ---------------- 强电结构 ----------------
const StrongConfigView = () => {
  const [cfg, setCfg] = useState('gridMeter');
  const options = [
    { value: 'gridMeter', label: '并网柜电表（默认）', desc: '强电接线图在并网柜处展示一只总电表' },
    { value: 'singleMeter', label: '单柜电表（多个电表）', desc: '强电接线图展示各储能柜单独电表' },
    { value: 'none', label: '不展示', desc: '强电接线图不展示电表' },
  ];
  return (
    <div className="panel" style={{ maxWidth: 760 }}>
      <div className="panel-title">强电结构<small>配置强电接线图中的储能表展示方式</small></div>
      <Radio.Group value={cfg} onChange={(e) => setCfg(e.target.value)} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {options.map((o) => (
          <Radio key={o.value} value={o.value}>
            <b>{o.label}</b>
            <div className="peak-note" style={{ marginTop: 2 }}>{o.desc}</div>
          </Radio>
        ))}
      </Radio.Group>
      <Button size="small" type="primary" style={{ marginTop: 14 }} onClick={() => message.success('强电结构配置已保存（演示）')}>保存</Button>
    </div>
  );
};

// ---------------- 日志管理 ----------------
const LogView = () => {
  const [sub, setSub] = useState('operation');
  const data = PEAK_LOGS;
  const logTable = (rows) => (
    <>
      <div className="peak-toolbar">
        <DatePicker.RangePicker size="small" style={{ width: 260 }} />
        <Button size="small" className="panel-title-btn" onClick={() => message.info('已按时间范围查询日志（演示）')}>查询</Button>
      </div>
      <Table size="small" rowKey="key" pagination={{ pageSize: 8, size: 'small' }} dataSource={rows}
        columns={[
          { title: '时间', dataIndex: 'time', width: 180 },
          { title: '类型', dataIndex: 'type', width: 120 },
          { title: '用户', dataIndex: 'user', width: 100 },
          { title: '动作', dataIndex: 'action' },
          { title: '目标', dataIndex: 'target', width: 140 },
          { title: '结果', dataIndex: 'result', width: 90, render: (v) => <Tag color={v === '成功' ? 'green' : 'orange'}>{v}</Tag> },
        ]}
      />
    </>
  );
  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return (
    <ScrollTabs
      activeKey={sub}
      onChange={setSub}
      items={[
        { key: 'operation', label: '操作日志', children: logTable(data.operation) },
        { key: 'env', label: '动环日志', children: logTable(data.env) },
        { key: 'ems', label: 'EMS 运行日志', children: logTable(data.ems) },
      ]}
    />
  );
};

// ---------------- EMS 时段管理 ----------------
const PeriodView = () => {
  const [rows, setRows] = useState(EMS_PERIODS);
  return (
    <div className="panel" style={{ maxWidth: 900 }}>
      <div className="panel-title">
        EMS 时段管理
        <Button size="small" className="panel-title-btn" style={{ float: 'right' }}
          onClick={() => setRows([...rows, { key: Date.now(), name: '新时段', start: '13:00', end: '19:00', action: '放电', power: '220 kW', enabled: '启用' }])}>新增时段</Button>
      </div>
      <Table size="small" rowKey="key" pagination={false} dataSource={rows}
        columns={[
          { title: '时段名称', dataIndex: 'name', render: (v, r) => <Input size="small" defaultValue={v} onChange={(e) => setRows(rows.map((x) => (x.key === r.key ? { ...x, name: e.target.value } : x)))} /> },
          { title: '开始', dataIndex: 'start', width: 110 },
          { title: '结束', dataIndex: 'end', width: 110 },
          { title: '动作', dataIndex: 'action', width: 90, render: (v) => <Tag color={v === '充电' ? 'cyan' : 'green'}>{v}</Tag> },
          { title: '功率', dataIndex: 'power', width: 110 },
          { title: '状态', dataIndex: 'enabled', width: 90 },
          { title: '操作', width: 90, render: (_, r) => <Button size="small" type="link" danger onClick={() => setRows(rows.filter((x) => x.key !== r.key))}>删除</Button> },
        ]}
      />
      <div className="peak-note">EMS 时段用于 EMS 侧的充放动作划分，与「电价管理」的分时时段相互独立。</div>
    </div>
  );
};

// ---------------- 电价管理 ----------------
const TariffView = () => {
  const table = (
    <>
      <div className="peak-toolbar">
        <Select size="small" defaultValue="10 月" style={{ width: 110 }} options={['9 月', '10 月', '11 月'].map((m) => ({ value: m, label: m }))} />
      </div>
      <Table size="small" rowKey="key" pagination={false} dataSource={TARIFF_ROWS}
        columns={[
          { title: '类型', dataIndex: 'kind', width: 100, render: (v) => <span className={`tariff-tag ${v === '谷' ? 'valley' : v === '平' ? 'flat' : v === '峰' ? 'peak' : 'sharp'}`}>{v}</span> },
          { title: '时段', dataIndex: 'period' },
          { title: '电价 (元/kWh)', dataIndex: 'price', width: 160, render: (v) => <InputNumber size="small" defaultValue={v} step={0.01} style={{ width: 120 }} /> },
        ]}
      />
      <div className="peak-note">电价分「尖 / 峰 / 平 / 谷 / 深谷」，支持按月与按日配置；电价管理结果用于收益报表与电量报表的分时计算。</div>
    </>
  );
  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return (
    <div className="panel" style={{ maxWidth: 900 }}>
      <div className="panel-title">电价管理</div>
      <ScrollTabs
        items={[
          { key: 'm', label: '月电价', children: table },
          { key: 'd', label: '日电价', children: table },
        ]}
      />
    </div>
  );
};

// ---------------- 日前电价 ----------------
const DayAheadPriceView = () => {
  const plan = getDayAhead().plan;
  const rows = plan.filter((_, i) => i % 2 === 0);
  return (
    <div className="panel" style={{ maxWidth: 900 }}>
      <div className="panel-title">日前电价<small>15 分钟颗粒度 · 供「控制中心 · 日前策略」使用</small></div>
      <div className="peak-toolbar">
        <DatePicker size="small" style={{ width: 180 }} />
        <Button size="small" className="panel-title-btn" onClick={() => message.success('日前电价已更新（演示）')}>更新日前电价</Button>
      </div>
      <Table size="small" rowKey="clock" pagination={false} dataSource={rows} scroll={{ y: 300 }}
        columns={[
          { title: '时刻', dataIndex: 'clock', width: 120 },
          { title: '日前电价 (元/kWh)', dataIndex: 'price', width: 180 },
          { title: '参考计划功率 (kW)', dataIndex: 'power', width: 180 },
        ]}
      />
    </div>
  );
};

// ---------------- 拓扑图配置（对齐 p048_i085 / p049_i086：组件区域 + 画布编辑器 + 属性区） ----------------
// 画布编辑器：点击/拖入图元添加，拖动移动，单击选中（青绿边框 + 橙角手柄 + 灰边锚点），
// 连线模式点击锚点连线，右键菜单（置顶/置底/上移/下移/四向对齐/复制/粘贴/剪切/删除），
// 快捷键 Delete / Ctrl+A / Ctrl+C / Ctrl+V / Ctrl+X / Ctrl+Z；选中图元右侧属性区可配置 ID 与设备选择。
const TOPO_BASIC = [
  { key: 'line', name: '直线', w: 130, h: 12 },
  { key: 'bus', name: '母线', w: 140, h: 24 },
  { key: 'rect', name: '矩形', w: 120, h: 60 },
  { key: 'triangle', name: '三角形', w: 70, h: 60 },
  { key: 'circle', name: '圆形', w: 56, h: 56 },
  { key: 'text', name: '文字', w: 120, h: 24 },
  { key: 'pcsText', name: 'PCS文字', w: 90, h: 24 },
  { key: 'bmsText', name: 'BMS文字', w: 90, h: 24 },
  { key: 'transformer', name: '变压器', w: 140, h: 40 },
];
const TOPO_COMP = [
  { key: 'meter', name: '储能表', w: 170, h: 46 },
  { key: 'pcs', name: 'PCS', w: 170, h: 46 },
  { key: 'bms', name: '电池簇', w: 200, h: 46 },
  { key: 'pv', name: '光伏节点', w: 170, h: 46 },
  { key: 'load', name: '负载节点', w: 170, h: 46 },
  { key: 'breaker', name: '断路器', w: 130, h: 40 },
];

const TopoNodeShape = ({ node, selected, connectMode, anchorFrom, onDown, onAnchor }) => {
  const { type, x, y, w, h, name } = node;
  const cx = x + w / 2;
  const cy = y + h / 2;
  let shape = null;
  switch (type) {
    case 'line':
      shape = <line x1={x} y1={cy} x2={x + w} y2={cy} stroke="#fff" strokeWidth={2} />;
      break;
    case 'bus':
      shape = <line x1={x} y1={cy} x2={x + w} y2={cy} stroke="#ff5a5f" strokeWidth={5} />;
      break;
    case 'rect':
    case 'breaker':
      shape = <rect x={x} y={y} width={w} height={h} rx={4} fill="none" stroke="#fff" strokeWidth={1.5} />;
      break;
    case 'triangle':
      shape = <polygon points={`${x},${y + h} ${x + w / 2},${y} ${x + w},${y + h}`} fill="none" stroke="#fff" strokeWidth={1.5} />;
      break;
    case 'circle':
      shape = <circle cx={cx} cy={cy} r={Math.min(w, h) / 2} fill="none" stroke="#fff" strokeWidth={1.5} />;
      break;
    case 'text':
      shape = <text x={cx} y={cy + 5} textAnchor="middle" className="pk-node-name" style={{ fontSize: 13 }}>{name}</text>;
      break;
    case 'pcsText':
    case 'bmsText':
      shape = <text x={cx} y={cy + 5} textAnchor="middle" className="pk-node-name" style={{ fontSize: 13, fontWeight: 700 }}>{type === 'pcsText' ? 'PCS' : 'BMS'}</text>;
      break;
    case 'transformer':
      shape = (<>
        <circle cx={cx} cy={cy} r={Math.min(w, h) / 2.4} fill="none" stroke="#fff" strokeWidth={1.5} />
        <text x={cx} y={cy + 4} textAnchor="middle" className="pk-node-name" style={{ fontSize: 12 }}>变</text>
      </>);
      break;
    case 'meter':
      shape = (<>
        <rect x={x} y={y} width={w} height={h} rx={6} fill="rgba(255,255,255,0.06)" stroke="#9fd8ff" strokeWidth={1.2} />
        <text x={cx} y={cy + 5} textAnchor="middle" className="pk-node-name" style={{ fontSize: 13 }}>Wh</text>
      </>);
      break;
    case 'pcs':
      shape = (<>
        <rect x={x} y={y} width={w} height={h} rx={6} fill="rgba(255,255,255,0.06)" stroke="#9fd8ff" strokeWidth={1.2} />
        <text x={cx} y={cy + 5} textAnchor="middle" className="pk-node-name" style={{ fontSize: 13 }}>~ ⎓</text>
      </>);
      break;
    case 'bms':
      shape = (<>
        <rect x={x} y={y} width={w} height={h} rx={6} fill="rgba(0,220,160,0.08)" stroke="#52e8b0" strokeWidth={1.2} />
        <text x={cx} y={cy + 5} textAnchor="middle" className="pk-node-name" style={{ fontSize: 13 }}>电池</text>
      </>);
      break;
    case 'pv':
    case 'load':
      shape = (<>
        <rect x={x} y={y} width={w} height={h} rx={6} fill="rgba(255,255,255,0.06)" stroke="#9fd8ff" strokeWidth={1.2} />
        <text x={cx} y={cy + 5} textAnchor="middle" className="pk-node-name" style={{ fontSize: 13 }}>{type === 'pv' ? '光伏' : '负载'}</text>
      </>);
      break;
    default:
      shape = <rect x={x} y={y} width={w} height={h} fill="none" stroke="#fff" strokeWidth={1.2} />;
  }
  return (
    <g
      onPointerDown={(e) => onDown(e, node.key)}
      style={{ cursor: connectMode ? 'crosshair' : 'move' }}
    >
      {shape}
      {name && type !== 'text' && type !== 'line' && type !== 'bus'
        ? <text x={cx} y={y + h + 15} textAnchor="middle" className="pk-node-name" style={{ fontSize: 12 }}>{name}</text>
        : null}
      {selected ? (
        <>
          <rect x={x - 2} y={y - 2} width={w + 4} height={h + 4} rx={8} fill="none" stroke="#00d4aa" strokeWidth={1.6} />
          {[[x - 3, y - 3], [x + w - 3, y - 3], [x - 3, y + h - 3], [x + w - 3, y + h - 3]].map(([hx, hy], i) => (
            <rect key={`h${i}`} x={hx} y={hy} width={6} height={6} fill="#ff8f1f" />
          ))}
          {[[cx, y - 3], [cx, y + h - 3], [x - 3, cy], [x + w - 3, cy]].map(([ax, ay], i) => (
            <circle
              key={`a${i}`} cx={ax} cy={ay} r={5}
              fill={connectMode && anchorFrom === node.key ? '#00d4aa' : '#cfd8e6'} stroke="#fff" strokeWidth={1}
              onPointerDown={(e) => onAnchor(e, node.key)}
              style={{ cursor: 'crosshair' }}
            />
          ))}
        </>
      ) : null}
    </g>
  );
};

const TopoConfigView = ({ station }) => {
  const dev = getDevices(station.id);
  const deviceOptions = [
    ...dev.meters.map((m) => ({ value: m.id, label: m.name })),
    ...dev.pcs.map((p) => ({ value: p.id, label: p.name })),
    ...dev.racks.map((r) => ({ value: r.id, label: r.name })),
    ...dev.hvac.map((a) => ({ value: a.id, label: a.name })),
  ];
  const initNodes = [
    { key: 'n-bus', type: 'bus', name: '母线', x: 250, y: 30, w: 140, h: 24, deviceId: null },
    { key: 'n-meter', type: 'meter', name: 'XT1#储能表', x: 240, y: 122, w: 170, h: 46, deviceId: 'M-02' },
    { key: 'n-pcs', type: 'pcs', name: 'XT1#PCS', x: 240, y: 228, w: 170, h: 46, deviceId: 'PCS-01' },
    { key: 'n-bms', type: 'bms', name: 'XT1#BMS-1#堆-1#BMS簇', x: 220, y: 334, w: 210, h: 46, deviceId: 'RACK-01' },
  ];
  const initLinks = [
    { from: 'n-bus', to: 'n-meter' },
    { from: 'n-meter', to: 'n-pcs' },
    { from: 'n-pcs', to: 'n-bms' },
  ];

  const [nodes, setNodes] = useState(initNodes);
  const [links, setLinks] = useState(initLinks);
  const [selected, setSelected] = useState([]);
  const [connectMode, setConnectMode] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [anchorFrom, setAnchorFrom] = useState(null);
  const [clipboard, setClipboard] = useState([]);
  const [history, setHistory] = useState([]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const svgRef = useRef(null);

  const nodeMap = {};
  nodes.forEach((n) => { nodeMap[n.key] = n; });

  const pushHist = () => setHistory((h) => [...h.slice(-19), JSON.stringify({ nodes, links })]);
  const undo = () => {
    if (!history.length) return;
    const prev = JSON.parse(history[history.length - 1]);
    setNodes(prev.nodes);
    setLinks(prev.links);
    setHistory(history.slice(0, -1));
  };

  const onNodeDown = (e, key) => {
    e.stopPropagation();
    setSelected((prev) => {
      if (e.ctrlKey || e.shiftKey) return prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      return [key];
    });
    if (!connectMode) {
      const n = nodeMap[key];
      pushHist();
      setDragging({ key, startX: e.clientX, startY: e.clientY, nodeX: n.x, nodeY: n.y });
    }
  };

  const onCanvasMove = (e) => {
    if (!dragging) return;
    const scale = 640 / e.currentTarget.getBoundingClientRect().width;
    const dx = (e.clientX - dragging.startX) * scale;
    const dy = (e.clientY - dragging.startY) * scale;
    setNodes((ns) => ns.map((n) => (n.key === dragging.key
      ? { ...n, x: Math.max(0, Math.round(dragging.nodeX + dx)), y: Math.max(0, Math.round(dragging.nodeY + dy)) }
      : n)));
  };

  const onCanvasUp = () => setDragging(null);

  const onAnchorDown = (e, key) => {
    e.stopPropagation();
    if (!connectMode) return;
    if (!anchorFrom) { setAnchorFrom(key); return; }
    if (anchorFrom === key) { setAnchorFrom(null); return; }
    pushHist();
    setLinks((ls) => [...ls.filter((l) => !(l.from === anchorFrom && l.to === key)), { from: anchorFrom, to: key }]);
    setAnchorFrom(null);
  };

  const updateNode = (key, patch) => setNodes((ns) => ns.map((n) => (n.key === key ? { ...n, ...patch } : n)));

  const addFromPalette = (it, e) => {
    let x = 40 + (nodes.length % 5) * 110;
    let y = 60 + Math.floor(nodes.length / 5) * 90;
    if (e && svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      const scale = 640 / rect.width;
      x = Math.round((e.clientX - rect.left) * scale) - it.w / 2;
      y = Math.round((e.clientY - rect.top) * scale) - it.h / 2;
    }
    pushHist();
    setNodes((ns) => [...ns, { key: `n-${Date.now()}`, type: it.key, name: it.name, x: Math.max(0, x), y: Math.max(0, y), w: it.w, h: it.h, deviceId: null }]);
  };

  const reorder = (keys, op) => {
    const arr = [...nodes];
    const set = new Set(keys);
    const picked = arr.filter((n) => set.has(n.key));
    const rest = arr.filter((n) => !set.has(n.key));
    if (op === 'top') return [...rest, ...picked];
    if (op === 'bottom') return [...picked, ...rest];
    const idxs = arr.map((n, i) => (set.has(n.key) ? i : -1)).filter((i) => i >= 0);
    const res = [...arr];
    if (op === 'up') {
      for (const i of idxs) { const j = i + 1; if (j < res.length && !set.has(res[j].key)) { [res[i], res[j]] = [res[j], res[i]]; } }
    } else {
      for (const i of [...idxs].reverse()) { const j = i - 1; if (j >= 0 && !set.has(res[j].key)) { [res[i], res[j]] = [res[j], res[i]]; } }
    }
    return res;
  };

  const ctxItems = [
    { key: 'top', label: '置顶' },
    { key: 'bottom', label: '置底' },
    { key: 'up', label: '上移一层' },
    { key: 'down', label: '下移一层' },
    { type: 'divider' },
    { key: 'align-left', label: '左对齐' },
    { key: 'align-right', label: '右对齐' },
    { key: 'align-top', label: '顶对齐' },
    { key: 'align-bottom', label: '底对齐' },
    { type: 'divider' },
    { key: 'copy', label: '复制' },
    { key: 'paste', label: '粘贴' },
    { key: 'cut', label: '剪切' },
    { key: 'delete', label: '删除' },
  ];

  const onCtx = ({ key }) => {
    const sel = nodes.filter((n) => selected.includes(n.key));
    if (!sel.length) { message.info('请先选择图元'); return; }
    pushHist();
    if (key === 'delete' || key === 'cut') {
      if (key === 'cut') setClipboard(sel);
      setNodes((ns) => ns.filter((n) => !selected.includes(n.key)));
      setSelected([]);
    } else if (key === 'copy') {
      setClipboard(sel);
    } else if (key === 'paste') {
      setNodes((ns) => [...ns, ...clipboard.map((c) => ({ ...c, key: `n-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, x: c.x + 24, y: c.y + 24 }))]);
    } else if (key === 'top' || key === 'bottom' || key === 'up' || key === 'down') {
      setNodes((ns) => reorder(selected, key));
    } else if (key.startsWith('align-')) {
      const dir = key.split('-')[1];
      const minX = Math.min(...sel.map((s) => s.x));
      const maxX = Math.max(...sel.map((s) => s.x + s.w));
      const minY = Math.min(...sel.map((s) => s.y));
      const maxY = Math.max(...sel.map((s) => s.y + s.h));
      setNodes((ns) => ns.map((n) => {
        if (!selected.includes(n.key)) return n;
        if (dir === 'left') return { ...n, x: minX };
        if (dir === 'right') return { ...n, x: maxX - n.w };
        if (dir === 'top') return { ...n, y: minY };
        return { ...n, y: maxY - n.h };
      }));
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const k = (e.key || '').toLowerCase();
      if (k === 'delete' || k === 'backspace') {
        if (selected.length) {
          pushHist();
          setNodes((ns) => ns.filter((n) => !selected.includes(n.key)));
          setSelected([]);
        }
      } else if (e.ctrlKey && k === 'a') {
        e.preventDefault();
        setSelected(nodes.map((n) => n.key));
      } else if (e.ctrlKey && k === 'c') {
        setClipboard(nodes.filter((n) => selected.includes(n.key)));
      } else if (e.ctrlKey && k === 'v') {
        if (clipboard.length) {
          pushHist();
          setNodes((ns) => [...ns, ...clipboard.map((c) => ({ ...c, key: `n-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, x: c.x + 24, y: c.y + 24 }))]);
        }
      } else if (e.ctrlKey && k === 'x') {
        pushHist();
        setClipboard(nodes.filter((n) => selected.includes(n.key)));
        setNodes((ns) => ns.filter((n) => !selected.includes(n.key)));
        setSelected([]);
      } else if (e.ctrlKey && k === 'z') {
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const selNode = selected.length === 1 ? nodeMap[selected[0]] : null;

  const renderPaletteItem = (it) => (
    <div
      key={it.key} className="pk-palette-item" title={`点击添加「${it.name}」`}
      draggable
      onDragStart={(e) => e.dataTransfer.setData('text/plain', it.key)}
      onClick={() => addFromPalette(it)}
    >
      <svg width={it.w * 0.32} height={it.h * 0.5} viewBox={`0 0 ${it.w} ${it.h}`} style={{ maxWidth: 64, maxHeight: 28 }}>
        <TopoNodeShape node={{ type: it.key, x: 0, y: 0, w: it.w, h: it.h, name: '' }} selected={false} onDown={() => {}} onAnchor={() => {}} />
      </svg>
      <span>{it.name}</span>
    </div>
  );

  return (
    <div className="pk-editor">
      <div className="panel pk-palette">
        <div className="panel-title">
          <span>组件区域</span>
          <Space size={8} style={{ float: 'right', color: '#9fd8ff' }}>
            <EditOutlined /><EnvironmentOutlined /><FolderOutlined />
          </Space>
        </div>
        <div className="pk-palette-group">基本元素</div>
        <div className="pk-palette-grid">{TOPO_BASIC.map(renderPaletteItem)}</div>
        <div className="pk-palette-group" style={{ cursor: 'pointer', marginTop: 12 }} onClick={() => setPaletteOpen(!paletteOpen)}>
          元器件 {paletteOpen ? '▲' : '▼'}
        </div>
        {paletteOpen ? <div className="pk-palette-grid">{TOPO_COMP.map(renderPaletteItem)}</div> : null}
        <div className="pk-palette-tip">点击或拖入画布添加图元</div>
      </div>

      <div className="panel pk-canvas-wrap">
        <div className="panel-title">
          <span>画布编辑器</span>
          <Space size={8} style={{ float: 'right' }}>
            <Button size="small" type={connectMode ? 'primary' : 'default'} onClick={() => { setConnectMode(!connectMode); setAnchorFrom(null); }}>
              {connectMode ? '退出连线模式' : '开启连线模式'}
            </Button>
            <Button size="small" className="panel-title-btn" onClick={() => { pushHist(); setNodes(initNodes.map((n) => ({ ...n }))); setLinks(initLinks.map((l) => ({ ...l }))); }}>画面居中</Button>
            <Button size="small" type="primary" onClick={() => message.success('拓扑图已保存（演示，无草稿）')}>保存</Button>
          </Space>
        </div>
        <Dropdown menu={{ items: ctxItems, onClick: onCtx }} trigger={['contextMenu']}>
          <div className="pk-canvas">
            <svg
              ref={svgRef} viewBox="0 0 640 460" className="pk-canvas-svg"
              onPointerDown={() => setSelected([])}
              onPointerMove={onCanvasMove}
              onPointerUp={onCanvasUp}
              onDrop={(e) => { e.preventDefault(); const k = e.dataTransfer.getData('text/plain'); if (k) { const it = TOPO_BASIC.concat(TOPO_COMP).find((x) => x.key === k); if (it) addFromPalette(it, e); } }}
              onDragOver={(e) => e.preventDefault()}
            >
              {links.map((l) => {
                const a = nodeMap[l.from];
                const b = nodeMap[l.to];
                if (!a || !b) return null;
                const p1 = { x: a.x + a.w / 2, y: a.y + a.h };
                const p2 = { x: b.x + b.w / 2, y: b.y };
                return (
                  <g key={`${l.from}-${l.to}`}>
                    <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#ff5a5f" strokeWidth={2} />
                    <circle cx={(p1.x + p2.x) / 2} cy={(p1.y + p2.y) / 2} r={3} fill="#cfd8e6" />
                  </g>
                );
              })}
              {nodes.map((n) => (
                <TopoNodeShape
                  key={n.key} node={n}
                  selected={selected.includes(n.key)}
                  connectMode={connectMode} anchorFrom={anchorFrom}
                  onDown={onNodeDown} onAnchor={onAnchorDown}
                />
              ))}
            </svg>
            {anchorFrom ? (
              <div className="pk-canvas-tip">已选择起点「{nodeMap[anchorFrom] ? nodeMap[anchorFrom].name : anchorFrom}」，点击另一个锚点完成连线</div>
            ) : null}
          </div>
        </Dropdown>
      </div>

      <div className="panel pk-prop">
        <div className="panel-title"><span>属性区</span><small>基础配置</small></div>
        {selNode ? (
          <>
            <div className="pk-form-row">
              <span className="pk-form-label">ID</span>
              <Input size="small" value={selNode.key} readOnly style={{ width: 130 }} />
            </div>
            <div className="pk-form-row">
              <span className="pk-form-label">设备选择</span>
              <Select
                size="small" value={selNode.deviceId || undefined} placeholder="请选择设备"
                style={{ width: 150 }} options={deviceOptions}
                onChange={(v) => updateNode(selNode.key, { deviceId: v })}
              />
            </div>
          </>
        ) : (
          <div className="pk-prop-empty">暂无选中组件</div>
        )}
      </div>
    </div>
  );
};

// ---------------- 二次口令 ----------------
const SecPwdView = () => {
  const policy = getSecPwdPolicy();
  const [modal, setModal] = useState(false);
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  return (
    <div className="panel" style={{ maxWidth: 900 }}>
      <div className="panel-title">
        二次口令
        <Button size="small" type="primary" style={{ float: 'right' }} onClick={() => setModal(true)}>修改二次口令</Button>
      </div>
      <div className="form-grid" style={{ marginBottom: 16 }}>
        <div className="form-item"><span className="form-item-label">口令规则</span><span className="form-item-value">{policy.rule}</span></div>
        <div className="form-item"><span className="form-item-label">口令有效期</span><span className="form-item-value">{policy.validDays} 天</span></div>
        <div className="form-item"><span className="form-item-label">连续错误锁定次数</span><span className="form-item-value">{policy.failLock} 次</span></div>
        <div className="form-item"><span className="form-item-label">锁定时长</span><span className="form-item-value">{policy.lockMinutes} 分钟</span></div>
        <div className="form-item"><span className="form-item-label">生效范围</span><span className="form-item-value">{policy.scope.join(' / ')}</span></div>
      </div>
      <Table size="small" rowKey="key" pagination={false} dataSource={SEC_PWD_RECORDS}
        columns={[
          { title: '时间', dataIndex: 'time' },
          { title: '用户', dataIndex: 'user', width: 100 },
          { title: '操作', dataIndex: 'action' },
          { title: 'IP', dataIndex: 'ip', width: 140 },
          { title: '结果', dataIndex: 'result', width: 190, render: (v) => <Tag color={v === '成功' ? 'green' : 'orange'}>{v}</Tag> },
        ]}
      />
      <Modal title="修改二次口令" open={modal} okText="提交" cancelText="取消"
        onCancel={() => setModal(false)}
        onOk={() => {
          if (!oldPwd || !newPwd) { message.warning('请输入原口令与新口令'); return; }
          message.success('二次口令已修改（演示）'); setModal(false); setOldPwd(''); setNewPwd('');
        }}
        destroyOnHidden
      >
        <div className="peak-form-row">
          <span className="peak-form-label">原口令</span>
          <Input.Password size="small" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} style={{ width: 240 }} />
        </div>
        <div className="peak-form-row">
          <span className="peak-form-label">新口令</span>
          <Input.Password size="small" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} style={{ width: 240 }} placeholder={policy.rule} />
        </div>
      </Modal>
    </div>
  );
};

const PeakSystem = ({ station }) => {
  const items = [
    { key: 'info', label: '电站信息管理', children: <StationInfoView station={station} /> },
    { key: 'device', label: '设备管理', children: <DeviceManageView station={station} /> },
    { key: 'strong', label: '强电结构', children: <StrongConfigView /> },
    { key: 'log', label: '日志管理', children: <LogView /> },
    { key: 'period', label: 'EMS 时段管理', children: <PeriodView /> },
    { key: 'tariff', label: '电价管理', children: <TariffView /> },
    { key: 'dayahead', label: '日前电价', children: <DayAheadPriceView /> },
    { key: 'topo', label: '拓扑图配置', children: <TopoConfigView station={station} /> },
    { key: 'pwd', label: '二次口令', children: <SecPwdView /> },
  ];
  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return <ScrollTabs items={items} />;
};

export default PeakSystem;
